import { memo } from "react"
import { Box, BoxProps, Stack, styled } from "@mui/material"
import { classes, getUIA } from "@tm/utils"
import { Notes } from "../../Notes"
import { TraderSelection } from "../../TraderSelection"
import { useDefaultArticleItemState } from "../../ContextProviderStates/DefaultArticleItemState"
import { Icon } from "../../../../muiStyled/DataDisplay/Icons"
import { Typography } from "../../../../muiStyled/DataDisplay/typographie"
import { Divider } from "../../../../muiStyled/DataDisplay/divider"
import { DefaultCompactArticleContentComponent } from "./CompactArticleContent"
import { DefaultExpandedArticleContentComponent } from "./ExpandedArticleContent"
import { TyresTile } from "../Tyres/components/TyresTile"
import { TileArticleContentComponent } from "./TileArticleContent"
import { useArticleItemState } from "../../ContextProviderStates"

export type DefaultArticleItemProps = BoxProps & {
    disabled?: boolean
}

export const ArticleWrapper = styled(Box)({
    marginBottom: 8,
}) as typeof Box

export const ArticleContainer = styled(Box)(({ theme }) => ({
    backgroundColor: "#fafafa",
    position: "relative",
    boxShadow: theme.shadows[3],
    width: "100%",
    "&.disabled": {
        marginTop: 0,
        pointerEvents: "none",
        opacity: theme.opacity?.disabled ?? 0.3,
    },
    "&:not(.tileView)": {
        margin: "8px 0 0 0",
    },
    "&.tileView.expanded, &.tyresTileView.expanded": {
        position: "sticky",
        zIndex: 4,
    },
    "&.tyresTileView, &.tileView": {
        display: "flex",
        flexDirection: "column",
        borderRadius: "3px",
    },
    "&.tyresTileView:not(.expanded), &.tileView:not(.expanded)": {
        height: "100%",
    },
}))

const DefaultArticleItemComponent = memo(({ disabled }: DefaultArticleItemProps) => {
    const expanded = useDefaultArticleItemState((x) => x.expanded)
    const isTyres = useArticleItemState((x) => x.type === "Tyres")
    const tileView = useDefaultArticleItemState((x) => x.options.tileView)

    const renderContent = () => {
        if (tileView) {
            return isTyres ? <TyresTile /> : <TileArticleContentComponent />
        }

        return expanded ? <DefaultExpandedArticleContentComponent /> : <DefaultCompactArticleContentComponent />
    }

    return (
        <ArticleWrapper>
            <ArticleContainer
                className={classes(disabled && "disabled", isTyres && tileView && "tyresTileView", tileView && "tileView", expanded && "expanded")}
            >
                {renderContent()}
            </ArticleContainer>
            <Notes />
            <TraderSelection />
        </ArticleWrapper>
    )
})

export const DefaultArticleItem = memo(() => {
    const disableReason = useDefaultArticleItemState((x) => x.options.disableReason)
    const articleOrder = useDefaultArticleItemState((x) => x.articleOrder)
    const articleId = useDefaultArticleItemState((x) => x.article.internalId)
    const tileView = useDefaultArticleItemState((x) => x.options.tileView)

    if (!disableReason) {
        if (tileView) {
            return <DefaultArticleItemComponent />
        }
        return (
            <Box order={articleOrder || 0} {...getUIA("Article", articleId)}>
                <DefaultArticleItemComponent />
            </Box>
        )
    }

    return (
        <Box order={articleOrder || 0}>
            <Stack direction="row" gap={1} alignItems="center" mt={1}>
                <Icon name="not" color="error" />
                <Typography>{disableReason}</Typography>
                <Box flex={1}>
                    <Divider light />
                </Box>
            </Stack>
            <Box height="100%">
                <DefaultArticleItemComponent disabled />
            </Box>
        </Box>
    )
})
